import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

/**
 * Public marketing /pricing page. Three plan cards, each anchored to
 * /onboard?plan=X so direct ad traffic lands in the signup funnel with
 * a plan preselected.
 *
 * Prices here are display-only — the actual subscription line item is
 * driven by the Stripe price IDs configured server-side. Keep these in
 * sync when prices change.
 */

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: "$99",
    cadence: "/month",
    blurb: "For new NEMT operators getting their first contracts off the ground.",
    features: [
      "Up to 10 drivers",
      "Driver onboarding + compliance",
      "1 broker contract",
      "Manual audit packets",
      "Email support",
    ],
  },
  {
    id: "professional",
    name: "Professional",
    price: "$199",
    cadence: "/month",
    badge: "Most popular",
    blurb: "For growing operators ready to scale broker relationships.",
    features: [
      "Up to 50 drivers",
      "Unlimited broker contracts",
      "Auto-emailed audit packets",
      "Bulk compliance nudges",
      "Ride compliance gating",
      "Tenant whitelabel branding",
      "Priority support",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "$299",
    cadence: "/month",
    blurb: "For multi-region operators and whitelabel resellers.",
    features: [
      "Unlimited drivers",
      "Multi-tenant platform view",
      "Custom domain + branding",
      "Dedicated audit inbox",
      "SLA + dedicated CSM",
      "Custom contract terms",
    ],
  },
];

const PlanCard = ({ plan, onStart, highlight }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 3,
      height: "100%",
      borderColor: highlight ? "#1F4E78" : "#e2e8f0",
      borderWidth: highlight ? 2 : 1,
      position: "relative",
      backgroundColor: "#ffffff",
    }}
  >
    {plan.badge ? (
      <Chip
        size="small"
        label={plan.badge}
        sx={{
          position: "absolute",
          top: -12,
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "#1F4E78",
          color: "#ffffff",
          fontWeight: 700,
        }}
      />
    ) : null}
    <CardContent sx={{ p: 4, display: "flex", flexDirection: "column", height: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>{plan.name}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        {plan.blurb}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mb: 2 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, color: "#0f172a" }}>
          {plan.price}
        </Typography>
        <Typography variant="body2" color="text.secondary">{plan.cadence}</Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Stack spacing={1} sx={{ flex: 1, mb: 3 }}>
        {plan.features.map((f) => (
          <Box key={f} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <CheckIcon sx={{ color: "#16a34a", fontSize: 18, mt: 0.25 }} />
            <Typography variant="body2">{f}</Typography>
          </Box>
        ))}
      </Stack>
      <Button
        variant={highlight ? "contained" : "outlined"}
        fullWidth
        onClick={onStart}
        sx={highlight ? { backgroundColor: "#1F4E78" } : {}}
      >
        Start free trial
      </Button>
    </CardContent>
  </Card>
);

export const PricingPage = () => {
  const navigate = useNavigate();
  return (
    <Stack
      spacing={4}
      sx={{
        p: { xs: 3, md: 6 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <Box sx={{ textAlign: "center", maxWidth: 720, mx: "auto" }}>
        <Typography variant="h3" sx={{ fontWeight: 800, color: "#0f172a", mb: 2 }}>
          Pricing
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Compliance, dispatch, and broker audits in one platform. 14-day free trial. Cancel anytime.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 3,
          maxWidth: 1100,
          mx: "auto",
          width: "100%",
          alignItems: "stretch",
        }}
      >
        {PLANS.map((p) => (
          <PlanCard
            key={p.id}
            plan={p}
            highlight={p.id === "professional"}
            onStart={() => navigate(`/onboard?plan=${p.id}`)}
          />
        ))}
      </Box>

      <Box sx={{ textAlign: "center", pt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Already have an account?{" "}
          <Box
            component="span"
            onClick={() => navigate("/login")}
            sx={{ color: "#1F4E78", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
          >
            Sign in
          </Box>
        </Typography>
      </Box>
    </Stack>
  );
};

export default PricingPage;
